import { useState, useEffect } from "react";
import parse from "html-react-parser";
import { useTranslation } from "react-i18next";
import { useThesaurusArea, useThesaurusTerm } from "@/hooks/api/useThesaurus";
import { usePhotos, useDocuments } from "@/hooks/api/useDocuments";
import { getImageUrl } from "@/utils/dedaloUtils";
import { useLocation, useSearchParams } from "react-router";
import useViewportWidth from "@/hooks/useViewportWidth";
import Layout from "@/components/Layout/Layout";
import Link from "@/components/LangLink/LangLink";
import Loading from "@/components/Loading/Loading";
import Links from "@/components/Resources/Links";
import Photos from "@/components/Resources/Photos";
import Videos from "@/components/Resources/Videos";
import Documents from "@/components/Resources/Documents";
import Bibliography from "@/components/Resources/Bibliography";

export default function Subarea({ id, parents }) {
    const parentArea = parents[2].split('_')[1]
    const parentBlock = parents[1].split('_')[1]
    const resWhiteBg = parents.length >= 3 ? true : false;
    const { t } = useTranslation();
    const viewportWidth = useViewportWidth();
    const { data, isLoading, error } = useThesaurusArea(id);
    const { data: parentAreaData } = useThesaurusTerm(parentArea);
    const { data: parentBlockData } = useThesaurusTerm(parentBlock);
    const documents_indexation_ids = JSON.parse(data?.[0]?.documents_indexation || '[]').join();
    const { data: photosData, isLoading: photosLoading, error: photosError } = usePhotos(documents_indexation_ids || '00');
    const { data: documentsData, isLoading: documentsLoading, error: documentsError } = useDocuments(documents_indexation_ids || '00');
    const index_locators = data?.[0]?.indexations ? JSON.parse(data[0].indexations) : [];
    const [fullText, setFullText] = useState(useViewportWidth >= 770);
    const [userExpanded, setUserExpanded] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const openResourceType = searchParams.get('recursos') || null;
    const parentBlockTerm = parentBlockData?.[0]?.term.split(':') || [];
    const parentBlockWebPath = parentBlockData?.[0]?.web_path || '';
    const sectionInfo = {title: parentBlockTerm[0], description: parentBlockTerm[1], slug: parentBlockWebPath};
    const parentAreaWebPath = parentAreaData?.[0]?.web_path || '';
    const term = data?.[0]?.term || '';
    const abstract = data?.[0]?.abstract || '';
    const body = data?.[0]?.body || '';
    const bodyWithHighlights = body.replaceAll('<strong>', '<p class="highlighted-p-1">').replaceAll('</strong>', '</p>');
    const imageUrl = data?.[0]?.image?.[0]?.image || '';
    const imageFootprint = data?.[0]?.image?.[0]?.footprint || '';
    const enlaces = data?.[0]?.uri || null;
    const bibliografia = data?.[0]?.bibliography_data || null;
    const photos = photosData?.filter(photo => photo.identify_image) || [];
    const documents = documentsData?.filter(doc => doc.identify_image) || [];

    const hasResources = photos?.length > 0 || documents?.length > 0 || index_locators?.length > 0 || enlaces || bibliografia?.length > 0;
    const location = useLocation();

    useEffect(() => {
        setUserExpanded(false);
    }, [location.pathname])

    useEffect(() => {
        if (userExpanded) return;
        setFullText(viewportWidth >= 770);
    }, [viewportWidth, userExpanded]);

    function resourceClickHandler(e) {
        const resourceType = e.currentTarget.dataset.resource;
        const p = new URLSearchParams(searchParams);
        p.set('recursos', resourceType);
        setSearchParams(p);
    }

    function closeResource() {
        const p = new URLSearchParams(searchParams);
        p.delete('recursos');
        setSearchParams(p);
    }

    if (isLoading || photosLoading || documentsLoading) return <Layout><Loading /></Layout>
    if (error || photosError || documentsError) return <Layout><Loading error={true}/></Layout>

    if (openResourceType === 'enlaces') return <Links section={sectionInfo} data={data} close={closeResource} white={resWhiteBg}/>;
    if (openResourceType === 'fotos') return <Photos section={sectionInfo} data={data} photos={photos} close={closeResource} white={resWhiteBg}/>;
    if (openResourceType === 'audiovisuales') return <Videos section={sectionInfo} data={data} index_locators={index_locators} close={closeResource} white={resWhiteBg}/>;
    if (openResourceType === 'documentos') return <Documents section={sectionInfo} data={data} documents={documents} close={closeResource} white={resWhiteBg}/>;
    if (openResourceType === 'bibliografia') return <Bibliography section={sectionInfo} data={data} close={closeResource} white={resWhiteBg}/>;

    return (
        <Layout section={sectionInfo}>
            <div className="has-background-white bottom-shadow py-7">
                <div className="wrapper is-relative">
                    <Link to={"/" + parentAreaWebPath} className="close-btn top-0" aria-label="Cerrar">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"><g fill="none" fillRule="evenodd" stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"><path d="M36 12 12 36M12 12l24 24"/></g></svg>
                    </Link>
                    <div className="columns gap-8 pt-6">
                        <div className={"column flow--l text-base" + (hasResources ? ' is-three-fifths' : '')}>
                            <h1 className="has-text-weight-semibold is-uppercase">{term}</h1>
                            {abstract && <div>{parse(abstract)}</div>}

                            <figure>
                                <div className="image-full-width">
                                    <img src={getImageUrl(imageUrl)} alt="" loading="lazy" />
                                </div>
                                <figcaption className="is-size-4">{imageFootprint}</figcaption>
                            </figure>

                            {body && (
                                fullText
                                    ? (
                                        <div>{parse(bodyWithHighlights)}</div>
                                    )
                                    : (
                                        <button
                                            className="button-reset is-size-5 is-uppercase has-text-weight-semibold"
                                            onClick={() => {setFullText(true); setUserExpanded(true);}}
                                        >
                                            {t('Leer mas')} <span className="is-sr-only">{`sobre ${term}`}</span>
                                        </button>
                                    )
                            )}

                        </div>

                        {hasResources && (<>
                            <div className="column flow--s">
                                <h2>{t('Recursos')}</h2>
                                <ul className="is-flex is-flex-wrap-wrap gap">
                                    {photos && photos.length > 0 && (
                                        <li><button className="button button--fotos" data-resource="fotos" onClick={resourceClickHandler}>{t('Fotos')}</button></li>
                                    )}
                                    {documents && documents.length > 0 && (
                                        <li><button className="button button--documentos" data-resource="documentos" onClick={resourceClickHandler}>{t('Documentos')}</button></li>
                                    )}
                                    {index_locators && index_locators.length > 0 && (
                                        <li><button className="button button--audiovisuales" data-resource="audiovisuales" onClick={resourceClickHandler}>{t('Audiovisuales')}</button></li>
                                    )}
                                    {enlaces && (
                                        <li><button className="button button--enlaces" data-resource="enlaces" onClick={resourceClickHandler}>{t('Enlaces')}</button></li>
                                    )}
                                    {bibliografia && bibliografia.length > 0 && (
                                        <li><button className="button button--bibliografia" data-resource="bibliografia" onClick={resourceClickHandler}>{t('Bibliografia')}</button></li>
                                    )}
                                </ul>
                            </div>
                        </>)}
                    </div>

                </div>
            </div>
        </Layout>
    )
}